import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import Swal from "sweetalert2";
import "../RessourcesStyles/ConsolidationSplitterModal.css";
import {
    buildSplitPayload,
    exportConsolidationSplit,
    fetchConsolidationSplitFormations,
    previewConsolidationSplit,
} from "../../../../services/consolidationSplitApi";

const DEFAULT_CRITERIA = ["period", "mode", "certifiante", "niveau"];

const CRITERIA_OPTIONS = [
    { key: "period", label: "Période" },
    { key: "mode", label: "Mode" },
    { key: "certifiante", label: "Certifiante" },
    { key: "niveau", label: "Niveau" },
];

const formatBoolean = (value) => {
    if (value == null) return "Non renseigne";
    return value ? "Oui" : "Non";
};

const sanitizeFileName = (value) =>
    String(value || "formation")
        .trim()
        .replace(/[^a-zA-Z0-9-_]+/g, "_")
        .replace(/^_+|_+$/g, "") || "formation";

const extractError = (error, fallback) => error?.message || fallback;

export default function ConsolidationSplitterModal({
    open,
    onClose,
    campagneId,
    dateDebut,
    dateFin,
}) {
    const [formations, setFormations] = useState([]);
    const [selectedFormationId, setSelectedFormationId] = useState("");
    const [criteria, setCriteria] = useState(DEFAULT_CRITERIA);
    const [personnesParGroupe, setPersonnesParGroupe] = useState("10");
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open || !campagneId) return;

        let alive = true;
        setLoading(true);
        setError("");
        setPreview(null);
        setCriteria(DEFAULT_CRITERIA);
        setPersonnesParGroupe("10");

        fetchConsolidationSplitFormations({ campagneId, dateDebut, dateFin })
            .then((data) => {
                if (!alive) return;
                const nextFormations = Array.isArray(data) ? data : [];
                setFormations(nextFormations);
                setSelectedFormationId((current) => {
                    if (current && nextFormations.some((item) => String(item.formationId) === String(current))) {
                        return current;
                    }
                    return nextFormations[0]?.formationId ? String(nextFormations[0].formationId) : "";
                });
            })
            .catch((err) => {
                if (!alive) return;
                setError(extractError(err, "Impossible de charger les formations a splitter."));
                setFormations([]);
                setSelectedFormationId("");
            })
            .finally(() => {
                if (alive) setLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [open, campagneId, dateDebut, dateFin]);

    useEffect(() => {
        if (!open) return;
        setPreview(null);
        setError("");
    }, [selectedFormationId, criteria, personnesParGroupe, open]);

    const selectedFormation = useMemo(
        () => formations.find((item) => String(item.formationId) === String(selectedFormationId)) || null,
        [formations, selectedFormationId]
    );

    const personnesParGroupeValue = Number(personnesParGroupe);
    const exportDisabled = !preview || preview.limitExceeded || exporting || loading;

    const handleToggleCriterion = (criterion) => {
        setCriteria((current) =>
            current.includes(criterion)
                ? current.filter((item) => item !== criterion)
                : [...current, criterion]
        );
    };

    const validateForm = () => {
        if (!selectedFormationId) {
            return "Veuillez séléctionner une formation.";
        }
        if (!personnesParGroupe || Number.isNaN(personnesParGroupeValue) || personnesParGroupeValue <= 0) {
            return "Veuillez saisir un nombre de personnes par groupe valide.";
        }
        return "";
    };

    const buildPayload = () =>
        buildSplitPayload({
            campagneId,
            formationId: Number(selectedFormationId),
            criteria,
            personnesParGroupe: personnesParGroupeValue,
            dateDebut,
            dateFin,
        });

    const handlePreview = async () => {
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        setError("");
        try {
            const response = await previewConsolidationSplit(buildPayload());
            setPreview(response);
        } catch (err) {
            setPreview(null);
            setError(extractError(err, "Impossible de prévisualiser le split."));
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setExporting(true);
        setError("");
        try {
            const response = await exportConsolidationSplit(buildPayload());
            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `bilan_split_${sanitizeFileName(preview?.formationName)}_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            Swal.fire({
                icon: "success",
                title: "Export terminé",
                text: "Le bilan de formation split a été téléchargé.",
            });
        } catch (err) {
            setError(extractError(err, "Impossible d'exporter le bilan split."));
        } finally {
            setExporting(false);
        }
    };

    if (!open) return null;

    return (
        <div className="consModal" onMouseDown={(event) => event.target === event.currentTarget && onClose?.()}>
            <div className="consModalPanel splitter-modal">
                <div className="consModalHeader splitter-header">
                    <div>
                        <div className="consModalTitle">Splitter les demandes de formation</div>
                        {/*<div className="splitter-subtitle">*/}
                        {/*    Analyse et export Excel sans modification des demandes en base.*/}
                        {/*</div>*/}
                    </div>
                    <button className="iconClose" type="button" onClick={onClose} title="Fermer">
                        <X size={18} />
                    </button>
                </div>

                <div className="consModalBody">
                    <label className="splitter-field" htmlFor="splitter-formation-select">
                        <span>Formation a splitter</span>
                        <select
                            id="splitter-formation-select"
                            className="splitter-select"
                            value={selectedFormationId}
                            onChange={(event) => setSelectedFormationId(event.target.value)}
                            disabled={loading || !formations.length}
                        >
                            {!formations.length && <option value="">Aucune formation disponible</option>}
                            {formations.map((formation) => (
                                <option key={formation.formationId} value={String(formation.formationId)}>
                                    {formation.formationName} ({formation.nombreDemandes} demandes)
                                </option>
                            ))}
                        </select>
                    </label>

                    <div className="splitter-summary">
                        <div className="splitter-summary-card">
                            <span className="splitter-summary-label">Nombre de demandes</span>
                            <strong>{selectedFormation?.nombreDemandes ?? 0}</strong>
                        </div>
                        <div className="splitter-summary-card">
                            <span className="splitter-summary-label">Maximum de groupes</span>
                            <strong>
                                {selectedFormation?.nombreMaxGroupes > 0 ? selectedFormation.nombreMaxGroupes : "Non limite"}
                            </strong>
                        </div>
                        <div className="splitter-summary-card">
                            <span className="splitter-summary-label">Groupes générés</span>
                            <strong>{preview?.nombreGroupes ?? 0}</strong>
                        </div>
                    </div>

                    <div className="splitter-section">
                        <div className="splitter-label">Critéres de split</div>
                        <div className="splitter-criteria">
                            {CRITERIA_OPTIONS.map((option) => (
                                <label key={option.key} className="splitter-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={criteria.includes(option.key)}
                                        onChange={() => handleToggleCriterion(option.key)}
                                    />
                                    <span>{option.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <label className="splitter-field" htmlFor="splitter-personnes-par-groupe">
                        <span>Nombre de personnes par groupe</span>
                        <input
                            id="splitter-personnes-par-groupe"
                            type="number"
                            min="1"
                            value={personnesParGroupe}
                            onChange={(event) => setPersonnesParGroupe(event.target.value)}
                            placeholder="Ex : 10"
                        />
                    </label>

                    {error ? <div className="splitter-warning">{error}</div> : null}
                    {preview?.limitExceeded ? (
                        <div className="splitter-warning">{preview.warningMessage}</div>
                    ) : null}

                    <div className="splitter-actions">
                        <button className="btnTop btnOutlineBlue" type="button" onClick={handlePreview} disabled={loading}>
                            {loading ? "Chargement..." : "Prévisualiser le split"}
                        </button>
                    </div>

                    {preview ? (
                        preview.groups?.length ? (
                            <div className="splitter-groups-grid">
                                {preview.groups.map((group) => (
                                    <div className="splitter-group-card" key={group.groupKey}>
                                        <div className="splitter-group-color-bar" style={{ backgroundColor: group.color }} />
                                        <div className="splitter-group-content">
                                            <div className="splitter-group-header">
                                                <h4>{group.groupLabel}</h4>
                                                <span>{group.count} personne(s)</span>
                                            </div>

                                            <div className="splitter-group-meta">
                                                <span>Periode : {group.period || "Non renseigne"}</span>
                                                <span>Mode : {group.mode || "Non renseigne"}</span>
                                                <span>Certifiante : {formatBoolean(group.certifiante)}</span>
                                                <span>Niveau : {group.niveau || "Non renseigne"}</span>
                                            </div>

                                            <ul className="splitter-users-list">
                                                {(group.demandes || []).map((demande) => (
                                                    <li key={demande.demandeId}>
                                                        <strong>{demande.collaborateur || "Collaborateur non renseigne"}</strong>
                                                        {demande.matricule ? <span> - {demande.matricule}</span> : null}
                                                        {demande.entite ? <small>{demande.entite}</small> : null}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="splitter-empty-state">
                                Aucune demande disponible pour cette formation avec les filtres actuels.
                            </div>
                        )
                    ) : null}
                </div>

                <div className="consModalFooter splitter-footer">
                    <button className="btnTop btnLight" type="button" onClick={onClose} disabled={exporting}>
                        Annuler
                    </button>
                    <button className="btnTop btnDark" type="button" onClick={handleExport} disabled={exportDisabled}>
                        {exporting ? "Export en cours..." : "Exporter Excel"}
                    </button>
                </div>
            </div>
        </div>
    );
}
